import React, { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  InputAdornment,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import moment from 'moment'
import useIsMobile from '../../../../helpers/useIsMobile'
import { CORMACK_LEHANE } from './catalogue'
import {
  completeEntryDetails,
  fieldVisible,
  findEntry,
  getAction,
  getForm,
  getFormInitialValues,
  missingRequiredFields,
  newLiveId,
} from './engine'
import { appendEntry, fmtTime, logKeyForEvent, staffNames } from './liveUi'
import useLiveRecord from './useLiveRecord'
import MedicineLinkPanel from './MedicineLinkPanel'

// ---------------------------------------------------------------------------
// Cormack-Lehane picker: schematic laryngoscopic views
// ---------------------------------------------------------------------------

const GradeSchematic = ({ grade }) => {
  const showFullGlottis = grade === '1'
  const showPartGlottis = grade === '2a'
  const showArytenoids = ['1', '2a', '2b'].includes(grade)
  const epiglottisUp = ['1', '2a', '2b', '3a'].includes(grade)
  const epiglottisDown = grade === '3b'
  return (
    <svg width="72" height="54" viewBox="0 0 72 54" aria-hidden="true">
      <ellipse
        cx="36"
        cy="27"
        rx="34"
        ry="25"
        fill="#F8D7D3"
        stroke="#B0706A"
      />
      {/* tongue / blade shadow from the top (larger = poorer view) */}
      <path
        d={
          grade === '4'
            ? 'M4 27 Q36 60 68 27 Q36 -6 4 27 Z'
            : grade.startsWith('3')
              ? 'M6 18 Q36 28 66 18 Q36 -4 6 18 Z'
              : 'M10 12 Q36 18 62 12 Q36 0 10 12 Z'
        }
        fill="#E8A9A2"
      />
      {showFullGlottis && <polygon points="36,20 25,44 47,44" fill="#3E2723" />}
      {showPartGlottis && (
        <polygon points="31,31 41,31 46,44 26,44" fill="#3E2723" />
      )}
      {(showFullGlottis || showPartGlottis) && (
        <>
          <line
            x1="35"
            y1={showFullGlottis ? 21 : 31}
            x2="27"
            y2="44"
            stroke="#fff"
            strokeWidth="1.5"
          />
          <line
            x1="37"
            y1={showFullGlottis ? 21 : 31}
            x2="45"
            y2="44"
            stroke="#fff"
            strokeWidth="1.5"
          />
        </>
      )}
      {showArytenoids && (
        <>
          <circle cx="28" cy="46" r="3.5" fill="#C98B84" stroke="#8D5550" />
          <circle cx="44" cy="46" r="3.5" fill="#C98B84" stroke="#8D5550" />
        </>
      )}
      {epiglottisUp && (
        <path
          d={
            grade.startsWith('3') ? 'M22 26 Q36 14 50 26' : 'M24 18 Q36 8 48 18'
          }
          fill="none"
          stroke="#8D5550"
          strokeWidth="3"
          strokeLinecap="round"
        />
      )}
      {epiglottisDown && (
        <path
          d="M20 34 Q36 26 52 34 Q36 40 20 34 Z"
          fill="#C98B84"
          stroke="#8D5550"
        />
      )}
    </svg>
  )
}

const GradePicker = ({ value, onChange }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
      gap: 1,
    }}
  >
    {CORMACK_LEHANE.map((option) => {
      const selected = value === option.value
      return (
        <ButtonBase
          key={option.value}
          onClick={() => onChange(selected ? undefined : option.value)}
          sx={{
            border: 2,
            borderColor: selected ? 'primary.main' : 'divider',
            bgcolor: selected ? 'action.selected' : 'background.paper',
            borderRadius: 2,
            p: 1,
            flexDirection: 'column',
            gap: 0.5,
            minHeight: 120,
          }}
        >
          <GradeSchematic grade={option.value} />
          <Typography fontWeight={700}>{option.label}</Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
          >
            {option.description}
          </Typography>
        </ButtonBase>
      )
    })}
  </Box>
)

// ---------------------------------------------------------------------------
// Field renderers
// ---------------------------------------------------------------------------

const bigChipSx = { height: 40, fontSize: '0.95rem', borderRadius: 2, px: 0.5 }

const FieldInput = ({ field, value, onChange, staff }) => {
  const label = field.label || field.key
  switch (field.type) {
    case 'select':
      return (
        <Stack direction="row" flexWrap="wrap" gap={1}>
          {(field.options || []).map((option) => (
            <Chip
              key={option}
              label={option}
              color={value === option ? 'primary' : 'default'}
              variant={value === option ? 'filled' : 'outlined'}
              onClick={() => onChange(value === option ? undefined : option)}
              sx={bigChipSx}
            />
          ))}
        </Stack>
      )
    case 'multi':
      return (
        <Autocomplete
          multiple
          freeSolo
          autoSelect
          options={field.options || []}
          value={Array.isArray(value) ? value : value ? [value] : []}
          onChange={(e, next) => onChange(next)}
          renderInput={(params) => (
            <TextField {...params} placeholder="Pick or type" />
          )}
        />
      )
    case 'number':
      return (
        <TextField
          type="number"
          value={value ?? ''}
          placeholder={field.placeholder}
          inputProps={{ inputMode: 'decimal', step: 'any' }}
          onChange={(e) =>
            onChange(e.target.value === '' ? undefined : Number(e.target.value))
          }
          InputProps={
            field.unit
              ? {
                  endAdornment: (
                    <InputAdornment position="end">{field.unit}</InputAdornment>
                  ),
                }
              : undefined
          }
          fullWidth
        />
      )
    case 'textarea':
      return (
        <TextField
          value={value ?? ''}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          multiline
          minRows={3}
          fullWidth
        />
      )
    case 'yesno':
      return (
        <ToggleButtonGroup
          exclusive
          value={value ?? null}
          onChange={(e, next) => onChange(next ?? undefined)}
        >
          {['Yes', 'No'].map((option) => (
            <ToggleButton
              key={option}
              value={option}
              sx={{ minWidth: 96, height: 48, fontWeight: 700 }}
              color={option === 'Yes' ? 'success' : 'error'}
            >
              {option}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      )
    case 'time':
      return (
        <Stack direction="row" gap={1} alignItems="center">
          <TextField
            type="time"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            inputProps={{ step: 60 }}
            sx={{ maxWidth: 180 }}
          />
          <Button
            variant="outlined"
            onClick={() => onChange(moment().format('HH:mm'))}
          >
            Now
          </Button>
        </Stack>
      )
    case 'grade':
      return <GradePicker value={value} onChange={onChange} />
    case 'staff':
      return (
        <Autocomplete
          freeSolo
          autoSelect
          options={staff}
          value={value ?? null}
          onChange={(e, next) => onChange(next || undefined)}
          renderInput={(params) => (
            <TextField {...params} placeholder="Pick crew or type a name" />
          )}
        />
      )
    case 'checklist': {
      const confirmed = Array.isArray(value) ? value : []
      const items = field.items || []
      return (
        <Stack gap={0.5}>
          <Button
            variant="outlined"
            size="small"
            sx={{ alignSelf: 'flex-start' }}
            onClick={() =>
              onChange(confirmed.length === items.length ? [] : [...items])
            }
          >
            {confirmed.length === items.length ? 'Clear all' : 'Confirm all'}
          </Button>
          {items.map((item) => (
            <FormControlLabel
              key={item}
              sx={{ alignItems: 'flex-start', py: 0.25 }}
              control={
                <Checkbox
                  checked={confirmed.includes(item)}
                  onChange={(e) =>
                    onChange(
                      e.target.checked
                        ? items.filter(
                            (x) => x === item || confirmed.includes(x),
                          )
                        : confirmed.filter((x) => x !== item),
                    )
                  }
                  sx={{ mt: -0.75 }}
                />
              }
              label={item}
            />
          ))}
        </Stack>
      )
    }
    case 'text':
    default:
      return (
        <TextField
          value={value ?? ''}
          placeholder={field.placeholder}
          onChange={(e) => onChange(e.target.value)}
          fullWidth
          aria-label={label}
        />
      )
  }
}

// ---------------------------------------------------------------------------
// Dialog
// ---------------------------------------------------------------------------

// `request`: { section, entryId, eventId, formKey, create }
//   entryId  - existing entry to complete / edit
//   eventId  - the tap (deferred entries are created from it)
//   create   - { moduleName, prefill, liveAction } for a manual entry
// Event-only forms (quick note) write to the event instead of an entry.
const LiveDetailForm = ({ request, onClose }) => {
  const { formValues, updateRecord } = useLiveRecord()
  const isMobile = useIsMobile('sm')
  const form = request ? getForm(request.formKey) : null
  const open = !!form
  const entry = useMemo(
    () =>
      request?.entryId && request.section
        ? findEntry(formValues, request.section, request.entryId)
        : null,
    // Only resolve when the request changes - linking stock replaces the entry
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [request],
  )
  const logKey = request?.eventId
    ? logKeyForEvent(formValues, request.eventId)
    : null
  const event = logKey
    ? formValues?.[logKey]?.events?.find((item) => item.id === request.eventId)
    : null

  const [values, setValues] = useState({})
  const [errors, setErrors] = useState([])
  const staff = useMemo(() => staffNames(formValues), [formValues?.incident])

  useEffect(() => {
    if (!form) return
    setErrors([])
    if (form.eventOnly) {
      setValues({ Note: event?.data?.note ?? '' })
      return
    }
    setValues(getFormInitialValues(form, entry ?? request.create?.prefill))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request])

  if (!open) return null

  const visibleFields = form.fields.filter((field) =>
    fieldVisible(field, values),
  )
  const action = getAction(
    entry?.liveAction || event?.type || request.create?.liveAction,
  )
  const when = entry?.dateTime || entry?.time || event?.time

  const setValue = (key, value) =>
    setValues((prev) => {
      const next = { ...prev }
      if (value === undefined) delete next[key]
      else next[key] = value
      return next
    })

  const save = () => {
    const missing = missingRequiredFields(form, values)
    if (missing.length) {
      setErrors(missing.map((field) => field.label || field.key))
      return
    }
    // Hidden (showIf) fields are cleared so stale answers don't linger
    const cleaned = {}
    form.fields.forEach((field) => {
      cleaned[field.key] = fieldVisible(field, values)
        ? values[field.key]
        : undefined
    })

    if (form.eventOnly) {
      const note = (values.Note ?? '').trim()
      if (logKey && request.eventId) {
        updateRecord((record) => ({
          ...record,
          [logKey]: {
            ...record[logKey],
            events: (record[logKey]?.events || []).map((item) =>
              item.id === request.eventId
                ? {
                    ...item,
                    detail: note,
                    data: { ...(item.data || {}), note },
                  }
                : item,
            ),
          },
        }))
      }
      onClose()
      return
    }

    const section = request.section
    if (request.create) {
      const entryId = newLiveId()
      const base = {
        moduleName: request.create.moduleName,
        [section === 'vitals' ? 'time' : 'dateTime']: new Date().toISOString(),
        entryId,
        liveAction: request.create.liveAction,
        source: 'manual',
        ...(request.create.prefill || {}),
      }
      updateRecord((record) =>
        completeEntryDetails(
          appendEntry(record, section, base),
          section,
          entryId,
          cleaned,
        ),
      )
    } else {
      const entryId = request.entryId || newLiveId()
      updateRecord((record) =>
        completeEntryDetails(record, section, entryId, cleaned, {
          eventId: request.eventId,
        }),
      )
    }
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      // Portal events bubble to React ancestors (clickable cards)
      onClick={(e) => e.stopPropagation()}
      onSubmit={(e) => e.stopPropagation()}
      PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Stack>
          <Typography variant="h6" fontWeight={700}>
            {form.title}
          </Typography>
          {(when || action) && (
            <Typography variant="body2" color="text.secondary">
              {[action?.label, event?.detail, when && fmtTime(when)]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack gap={2.5}>
          {form.medicine &&
            request.section === 'treatment' &&
            entry &&
            !entry.medicineLogId && (
              <>
                <MedicineLinkPanel
                  entry={entry}
                  values={values}
                  onLinked={onClose}
                />
                <Divider />
              </>
            )}
          {visibleFields.map((field) => (
            <Stack key={field.key} gap={0.75}>
              <Typography fontWeight={600}>
                {field.label || field.key}
                {field.required && (
                  <Typography component="span" color="error.main">
                    {' *'}
                  </Typography>
                )}
              </Typography>
              <FieldInput
                field={field}
                value={values[field.key]}
                onChange={(value) => setValue(field.key, value)}
                staff={staff}
              />
              {field.helper && (
                <Typography variant="caption" color="text.secondary">
                  {field.helper}
                </Typography>
              )}
            </Stack>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          py: 1.5,
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: 1,
        }}
      >
        {errors.length > 0 && (
          <Alert severity="error">Please complete: {errors.join(', ')}</Alert>
        )}
        <Stack direction="row" gap={1} justifyContent="flex-end">
          <Button onClick={onClose} size="large">
            Cancel
          </Button>
          <Button
            onClick={save}
            variant="contained"
            color="success"
            size="large"
          >
            Save details
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  )
}

export default LiveDetailForm
